import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MenuItem, Select } from '@mui/material';
import { Flex, Typo, Button, Textinput, SwitchRow, theme, Figure } from '../../../ui';
import { toRgba } from '../../../ui/theme/colors';
import ImageUploader from '../../media/uploaders/ImageUploader';
import FileUploader from '../../media/uploaders/FileUploader';
import UcpPeriodSelector from '../../../components/UcpPeriodSelector';
import { getPeriodByTime } from '../../../utils/ucpPeriods';
import { useCourseDurationsState } from '../../course/hooks';
import { useFeatures } from '../../settings/hooks';
import { courseAccessTimePeriod, secondsToPeriod } from '../../course/courseUtils';

const Container = styled(Flex)`
  height: 100%;
  overflow: hidden;
`;

const ScrollContent = styled(Flex)`
  padding: 20px;
  padding-bottom: 0;
  flex: 1;
  overflow-y: auto;
`;

const SettingsSection = styled(Flex)`
  padding-bottom: 16px;
  margin-bottom: 16px;
  width: 100%;
`;

const DiplomaInfo = styled(Flex)`
  background: ${theme.colors.NEU_100};
  padding: 12px;
  border-radius: 8px;
  width: 100%;
`;

const SectionDelayItem = styled(Flex)`
  background: ${theme.colors.NEU_100};
  padding: 12px;
  border-radius: 8px;
  width: 100%;
`;

const DelayInput = styled(Textinput)`
  max-width: 60px;
`;

const DelaySelect = styled(Select)`
  min-width: 100px;
`;

const Seconds = {
  hour: 3600,
  day: 86400,
  month: 2592000,
};

const ButtonsContainer = styled(Flex)`
  padding: 16px 20px;
  border-top: 1px solid ${theme.colors.NEU_200};
  background: ${theme.colors.NEU_0};
  flex-shrink: 0;
`;

const StatusBanner = styled.div`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $isDraft }) => ($isDraft ? toRgba(theme.colors.WARNING_YELLOW, 0.08) : toRgba(theme.colors.SUCCESS_GREEN, 0.08))};

  &:hover {
    background: ${({ $isDraft }) => ($isDraft ? toRgba(theme.colors.WARNING_YELLOW, 0.15) : toRgba(theme.colors.SUCCESS_GREEN, 0.15))};
  }
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $isDraft }) => ($isDraft ? theme.colors.WARNING_YELLOW : theme.colors.SUCCESS_GREEN)};
`;

/**
 * CourseSettingsPanel - Panel for editing course settings in the course editor
 *
 * @param {Object} props
 * @param {Object} props.course - The course being edited
 * @param {Function} props.onSave - Callback when settings are saved
 * @param {Function} props.onClose - Callback when panel closes
 * @param {Function} props.onToggleDraft - Callback when draft status is toggled
 * @returns {JSX.Element}
 */
const CourseSettingsPanel = ({ course, onSave, onClose, onToggleDraft }) => {
  const { t } = useTranslation();
  const features = useFeatures();
  const courseDurations = useCourseDurationsState(true);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [duration, setDuration] = useState({});
  const [isDraft, setIsDraft] = useState(false);
  const [gatedAccess, setGatedAccess] = useState(false);
  const [delayedAccess, setDelayedAccess] = useState(false);
  const [diploma, setDiploma] = useState(null);
  const [passPercentage, setPassPercentage] = useState(80);
  const [sectionDelays, setSectionDelays] = useState({});

  // Get sections from course TOC (excluding diploma section)
  const courseSections = useMemo(
    () => course?.toc?.filter((section) => section.type !== 'diploma' && section.id !== 'diploma-section') || [],
    [course?.toc],
  );

  // Initialize state when course changes
  useEffect(() => {
    if (course) {
      setName(course.name || '');
      setDescription(course.description || '');
      setCoverImage(course.coverImage || null);
      setIsDraft(course.draft || false);
      setGatedAccess(course.settings?.dripContent?.gatedAccess || false);
      setDelayedAccess(course.settings?.dripContent?.delayedAccess || false);

      const ucpPeriod = getPeriodByTime(course.ucpPeriod);
      if (ucpPeriod) {
        setDuration(ucpPeriod);
      }

      if (course.settings?.diploma?.fileId) {
        setDiploma(course.settings.diploma);
        setPassPercentage(course.settings.diploma.passPercentage || 80);
      } else {
        setDiploma(null);
      }

      // Initialize section delays from TOC
      if (course.toc?.length) {
        const delays = {};
        course.toc.forEach((section) => {
          if (section.type !== 'diploma' && section.id !== 'diploma-section') {
            const period = secondsToPeriod(section.accessDelay || 0);
            delays[section.id] = { value: period.amount || 0, type: period.type || 'day' };
          }
        });
        setSectionDelays(delays);
      }
    }
  }, [course]);

  const handleSaveImage = useCallback((image) => {
    if (image) {
      setCoverImage({ id: image.id, url: image.url });
    }
  }, []);

  const handleSaveDiploma = useCallback(
    (file) => {
      if (file) {
        setDiploma({ fileId: file.id, passPercentage });
      }
    },
    [passPercentage],
  );

  const handleRemoveDiploma = useCallback(() => {
    setDiploma(null);
  }, []);

  const handleDraftToggle = useCallback(() => {
    const newDraftState = !isDraft;
    setIsDraft(newDraftState);
    if (onToggleDraft) {
      onToggleDraft({ id: course.id });
    }
  }, [isDraft, course?.id, onToggleDraft]);

  const courseId = course?.id;

  const handleDelayChange = useCallback((sectionId, property, value) => {
    setSectionDelays((prev) => {
      const currentDelay = prev[sectionId] || { value: 0, type: 'day' };
      return {
        ...prev,
        [sectionId]: {
          ...currentDelay,
          [property]: value,
        },
      };
    });
  }, []);

  const handleDelayedAccessToggle = useCallback((isChecked) => {
    setDelayedAccess(isChecked);

    if (!isChecked) {
      const resetDelays = {};
      courseSections.forEach((section) => {
        resetDelays[section.id] = { value: 0, type: 'day' };
      });
      setSectionDelays(resetDelays);
    }
  }, [courseSections]);

  const handleSave = useCallback(() => {
    const categories = course.categories?.map((cat) => (typeof cat === 'string' ? cat : cat.slug)) || [];
    const sectionUpdates = courseSections.map((section) => {
      const delay = delayedAccess
        ? (sectionDelays[section.id] || { value: 0, type: 'day' })
        : { value: 0, type: 'day' };

      return {
        id: section.id,
        courseId,
        accessDelay: Number(delay.value || 0) * Seconds[delay.type || 'day'],
      };
    });

    const courseData = {
      id: course.id,
      name,
      description,
      categories,
      ucpPeriod: duration?.time || course.ucpPeriod,
      coverImage: coverImage?.id ? { id: coverImage.id } : undefined,
      settings: {
        ...course.settings,
        dripContent: {
          ...course.settings?.dripContent,
          gatedAccess,
          delayedAccess,
        },
        diploma: diploma
          ? {
              fileId: diploma.fileId,
              passPercentage,
            }
          : { fileId: null },
      },
    };

    if (onSave) {
      onSave({ courseData, sectionUpdates });
    }
  }, [course, name, description, coverImage, duration, gatedAccess, delayedAccess, diploma, passPercentage, onSave, courseSections, sectionDelays, courseId]);

  if (!course) {
    return (
      <Container direction='column' align='flex-start'>
        <ScrollContent direction='column' gap='20' align='flex-start'>
          <Typo.TextTitle>{t('course.courseSettings')}</Typo.TextTitle>
          <Typo.SmallText>{t('course.noCourseSelected')}</Typo.SmallText>
        </ScrollContent>
      </Container>
    );
  }

  return (
    <Container direction='column' align='flex-start'>
      <ScrollContent direction='column' gap='20' align='flex-start'>
        <Flex direction='column' gap='4' align='flex-start' width='100%'>
          <Typo.ContentSmallHeader>{t('globals.settings')}</Typo.ContentSmallHeader>
        </Flex>

        {/* Draft / Ready Status */}
        {features.draftCourses && (
          <StatusBanner $isDraft={isDraft} onClick={handleDraftToggle}>
            <Flex direction='column' gap='4' width='100%'>
              <Flex align='center' justify='space-between' width='100%'>
                <Flex align='center' gap='8'>
                  <StatusDot $isDraft={isDraft} />
                  <Typo.SmallText>{isDraft ? t('globals.draft') : t('globals.ready')}</Typo.SmallText>
                </Flex>
                <Typo.SmallText color={theme.colors.NEU_600}>{t('globals.clickToChange')}</Typo.SmallText>
              </Flex>
              <Typo.SmallText color={theme.colors.NEU_600}>{isDraft ? t('course.draftDescription') : t('course.readyDescription')}</Typo.SmallText>
            </Flex>
          </StatusBanner>
        )}

        <SettingsSection direction='column' gap='12' align='flex-start'>
          <Typo.TextTitle $textAlign='left' $marginBottom='10px'>
            {t('course.courseInfo')}
          </Typo.TextTitle>

          <ImageUploader
            type={'single'}
            id='courseSettingsImage'
            saveAction={handleSaveImage}
            imageToPreview={coverImage?.url}
            label={t('settings.changeImage')}
            iconName='Edit'
          >
            {coverImage?.url && (
              <Figure.Rectangle>
                <img alt={t('course.coverImageAlt')} src={coverImage?.url} loading='lazy' />
              </Figure.Rectangle>
            )}
          </ImageUploader>

          {/* Course Name */}
          <Textinput label={t('course.courseName')} value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          {/* Course Description */}
          <Textinput label={t('course.courseDesc')} value={description} onChange={(e) => setDescription(e.target.value)} isMultiline minRows={3} fullWidth />
          {/* Access Duration */}
          {courseDurations && courseDurations.length > 0 && <UcpPeriodSelector duration={duration} options={courseDurations} setDuration={setDuration} />}
        </SettingsSection>

        {/* Drip Content */}
        <SettingsSection direction='column' gap='12' align='flex-start'>
          <Typo.TextTitle $textAlign='left' $marginBottom='10px'>
            {t('course.dripContent')}
          </Typo.TextTitle>

          <SwitchRow
            label={t('course.enableGateAccess')}
            checked={gatedAccess}
            onChange={(e) => setGatedAccess(e.target.checked)}
            enabledText={t('globals.enabled')}
            disabledText={t('globals.disabled')}
          />
          <Typo.SmallText color={theme.colors.NEU_600}>{t('course.gateAccessDescription')}</Typo.SmallText>

          <SwitchRow
            label={t('course.delayedAccess')}
            checked={delayedAccess}
            onChange={(e) => handleDelayedAccessToggle(e.target.checked)}
            enabledText={t('globals.enabled')}
            disabledText={t('globals.disabled')}
          />
          <Typo.SmallText color={theme.colors.NEU_600}>{t('course.delayedAccessDescription')}</Typo.SmallText>

          {/* Section delay settings - shown when delayed access is enabled */}
          {delayedAccess &&
            courseSections.map((section) => (
              <SectionDelayItem key={section.id} direction='column' gap='8' align='flex-start'>
                <Typo.SmallText>{section.name}</Typo.SmallText>
                <Typo.SmallText color={theme.colors.NEU_600}>{t('course.makeItAvailableAfter')}</Typo.SmallText>
                <Flex gap='8' align='center'>
                  <DelayInput
                    type='number'
                    value={sectionDelays[section.id]?.value || 0}
                    onChange={(e) => handleDelayChange(section.id, 'value', Number(e.target.value))}
                    variant='standard'
                  />
                  <DelaySelect
                    variant='standard'
                    value={sectionDelays[section.id]?.type || 'day'}
                    onChange={(e) => handleDelayChange(section.id, 'type', e.target.value)}
                  >
                    {courseAccessTimePeriod.map((period) => (
                      <MenuItem key={period.value} value={period.value}>
                        {period.text}
                      </MenuItem>
                    ))}
                  </DelaySelect>
                </Flex>
              </SectionDelayItem>
            ))}
        </SettingsSection>

        {/* Diploma */}
        {features.diplomas && (
          <SettingsSection direction='column' gap='12' align='flex-start'>
            <Typo.TextTitle>{t('globals.diploma')}</Typo.TextTitle>

            {diploma?.fileId ? (
              <>
                <DiplomaInfo direction='column' gap='8' align='flex-start'>
                  <Typo.SmallText>{t('course.diplomaUploaded')}</Typo.SmallText>
                  <Flex gap='12' align='center' width='100%'>
                    <Typo.SmallText>{t('course.percentageForDiploma')}</Typo.SmallText>
                    <Textinput
                      type='number'
                      value={passPercentage}
                      onChange={(e) => setPassPercentage(Number(e.target.value))}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <Typo.SmallText>%</Typo.SmallText>
                  </Flex>
                </DiplomaInfo>
                <Button variant='ghost' onClick={handleRemoveDiploma}>
                  {t('course.removeDiploma')}
                </Button>
              </>
            ) : (
              <>
                <Typo.SmallText color={theme.colors.NEU_600}>{t('settings.UploadDiplomaHelper')}</Typo.SmallText>
                <FileUploader
                  id='diplomaTemplatePanel'
                  saveAction={handleSaveDiploma}
                  fileType='other'
                  label={t('settings.uploadTemplate')}
                  iconName='Upload'
                  variant='customized'
                />
              </>
            )}
          </SettingsSection>
        )}
      </ScrollContent>

      <ButtonsContainer gap='10' width='100%' justify='flex-end'>
        {onClose && (
          <Button onClick={onClose} variant='ghost'>
            {t('globals.close')}
          </Button>
        )}
        <Button onClick={handleSave}>{t('globals.save')}</Button>
      </ButtonsContainer>
    </Container>
  );
};

export default CourseSettingsPanel;
